import { lazy, Suspense, useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { useT } from '@/i18n/I18nProvider';
import { useCurrentUser } from '@/features/auth/auth.store';
import { usePermission } from '@/features/auth/permissions';
import { Button, Tooltip } from '@/shared/ui';
import { AI_ASSISTANT_PERMISSIONS } from '../aiAccess';
import '../ai.css';
import { useAssistantStore } from '../chat/assistant.store';

// The panel (markdown renderer, composer) is only fetched the first time it is opened.
const AssistantPanel = lazy(() => import('./AssistantPanel'));

/**
 * Global entry to the "Zapytaj NEXOPS" assistant: floating gradient button (bottom-right), the Ctrl/⌘+J shortcut
 * and the right-hand panel. Renders nothing without `ai.insights` or `logistics.insights`. Mounted once in `AppLayout`.
 */
export function AiAssistant() {
  const t = useT();
  const allowed = usePermission(AI_ASSISTANT_PERMISSIONS);
  const userId = useCurrentUser()?.id ?? null;
  const open = useAssistantStore((s) => s.open);
  const [everOpened, setEverOpened] = useState(open);

  // A conversation belongs to one user: drop it on logout / demo user switch.
  useEffect(() => {
    const { ownerId, reset, setOpen } = useAssistantStore.getState();
    if (ownerId !== userId) {
      reset(userId);
      if (!userId) setOpen(false);
    }
  }, [userId]);

  useEffect(() => {
    if (open) setEverOpened(true);
  }, [open]);

  useEffect(() => {
    if (!allowed) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'j') {
        e.preventDefault();
        useAssistantStore.getState().toggle();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [allowed]);

  if (!allowed) return null;

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => useAssistantStore.getState().setOpen(true)}
          aria-label={t('ai.assistant.open')}
          className="ai-fab brand-gradient fixed bottom-4 right-4 z-40 flex h-14 items-center gap-2.5 rounded-full px-4 text-[14px] font-bold text-white transition-transform hover:-translate-y-0.5 focus-visible:ring-4 focus-visible:ring-ring/40 sm:bottom-6 sm:right-6 print:hidden"
        >
          <Sparkles className="size-5" aria-hidden />
          <span className="hidden sm:inline">{t('ai.assistant.title')}</span>
        </button>
      )}
      {everOpened && (
        <Suspense fallback={null}>
          <AssistantPanel />
        </Suspense>
      )}
    </>
  );
}

/** Topbar button that toggles the assistant (hidden without `ai.insights` / `logistics.insights`). */
export function AiTopbarButton() {
  const t = useT();
  const allowed = usePermission(AI_ASSISTANT_PERMISSIONS);
  const open = useAssistantStore((s) => s.open);
  if (!allowed) return null;
  const toggle = () => useAssistantStore.getState().toggle();
  return (
    <>
      <Button variant="secondary" size="md" className="hidden gap-2 md:inline-flex 2xl:pr-2.5" onClick={toggle} aria-pressed={open} aria-label={t('ai.assistant.topbar')}>
        <Sparkles className="text-primary" aria-hidden />
        <span className="hidden 2xl:inline">{t('ai.assistant.topbar')}</span>
        <kbd className="hidden rounded border border-border bg-muted px-1.5 font-sans text-2xs font-medium text-muted-foreground 2xl:inline">{t('ai.assistant.shortcut')}</kbd>
      </Button>
      <Tooltip content={t('ai.assistant.topbar')}>
        <Button variant="ghost" size="icon" className="md:hidden" onClick={toggle} aria-label={t('ai.assistant.open')} aria-pressed={open}>
          <Sparkles className="text-primary" />
        </Button>
      </Tooltip>
    </>
  );
}
